import { AWSAccountConfig, AwsAccount, RoleValidationResult, CustomerAccountsResponse } from '../../../types';
import type {
  AccountBaselineRunResponse,
  AccountBaselineSettings,
  AccountBaselineStatusResponse,
} from '../../../types/backend';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';
import { useMemo } from 'react';

export const createAwsAccountService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    createCustomerAwsAccount: async (customerId: string, config: AWSAccountConfig): Promise<AwsAccount> =>
      rpcCall(client, 'customers.CreateAWSAccount', { ...config, customer_id: customerId }),

    getCustomerAwsAccounts: async (customerId: string): Promise<CustomerAccountsResponse> =>
      rpcCall<CustomerAccountsResponse>(client, 'customers.ListAWSAccounts', { customer_id: customerId }),

    updateCustomerAwsAccount: async (customerId: string, accountId: string, config: Partial<AWSAccountConfig>): Promise<AwsAccount> =>
      rpcCall(client, 'customers.UpdateAWSAccount', { customer_id: customerId, account_id: accountId, ...config }),

    setAwsAccountAdminRoles: async (
      customerId: string,
      accountId: string,
      roles: { automation_role_name?: string; onboarding_role_name?: string },
    ): Promise<AwsAccount> =>
      rpcCall(client, 'customers.SetAWSAccountAdminRoles', {
        customer_id: customerId,
        account_id: accountId,
        ...roles,
      }),

    setAccountBaselineManaged: async (customerId: string, accountId: string, managed: boolean): Promise<AccountBaselineSettings> =>
      rpcCall<AccountBaselineSettings>(client, 'account_baseline.SetAccountBaselineManaged', {
        customer_id: customerId,
        account_id: accountId,
        baseline_managed: managed,
      }),

    setAccountBreakGlass: async (customerId: string, accountId: string, required: boolean): Promise<AccountBaselineSettings> =>
      rpcCall<AccountBaselineSettings>(client, 'account_baseline.SetAccountBreakGlass', {
        customer_id: customerId,
        account_id: accountId,
        break_glass_required: required,
      }),

    setAccountOptionalBaseline: async (
      customerId: string, accountId: string, baseline: string, enabled: boolean,
    ): Promise<AccountBaselineSettings> =>
      rpcCall<AccountBaselineSettings>(client, 'account_baseline.SetAccountOptionalBaseline', {
        customer_id: customerId,
        account_id: accountId,
        baseline,
        enabled,
      }),

    runAccountBaseline: async (customerId: string, accountId: string): Promise<AccountBaselineRunResponse> =>
      rpcCall<AccountBaselineRunResponse>(client, 'account_baseline.RunAccountBaseline', {
        customer_id: customerId,
        account_id: accountId,
      }),

    deleteCustomerAwsAccount: async (customerId: string, accountId: string): Promise<void> => {
      await rpcCall(client, 'customers.DeleteAWSAccount', { customer_id: customerId, account_id: accountId });
    },

    getAccountBaselineStatus: async (customerId: string): Promise<AccountBaselineStatusResponse> =>
      rpcCall<AccountBaselineStatusResponse>(client, 'account_baseline.GetAccountBaselineStatus', {
        customer_id: customerId,
      }),

    testCustomerRoleAccess: async (customerId: string, accountId: string, roleType: 'readonly' | 'write' | 'automation', region: string): Promise<RoleValidationResult> => {
      // The backend validates this against the account's supported_regions, so it must
      // be a region the customer account actually supports, not the control-plane one.
      const awsOperation = {
        service_name: 'sts',
        operation_name: 'get_caller_identity',
        parameters: {},
        region,
      };
      return rpcCall(client, 'customers.ValidateRoleAccess', {
        customer_id: customerId, account_id: accountId, role_type: roleType, aws_operation: awsOperation,
      });
    },

    getConsoleAccess: async (
      customerId: string,
      accountId: string,
      roleType: 'readonly' | 'write' = 'readonly',
      destination?: string
    ): Promise<{ signin_url: string; expires_in: number; account_id: string; role_type: string; destination: string }> =>
      rpcCall(client, 'customers.GetConsoleAccess', {
        customer_id: customerId, account_id: accountId, role_type: roleType, destination,
      }),

    getCliCredentials: async (
      customerId: string,
      accountId: string,
      roleType: 'readonly' | 'write' = 'readonly',
    ): Promise<{ access_key_id: string; secret_access_key: string; session_token: string; expiration: string; region: string; account_id: string; role_type: string }> =>
      rpcCall(client, 'customers.GetCliCredentials', {
        customer_id: customerId, account_id: accountId, role_type: roleType,
      }),
  };
};

export const useAwsAccountService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('Not authenticated');
    }

    return createAwsAccountService(idToken);
  }, [idToken]);
};
